import {
  getFontCss,
  HoverableLinkSizes,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React from 'react'
import { useSelector } from 'react-redux'
import AnimatedTableChevron from 'src/components/AnimatedTableChevron/AnimatedTableChevron'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import SvgArrowForward from 'src/components/Svgs/SvgArrowForward'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { useCurrencyFormatter } from 'src/localization/Crncy'
import { useTranslate } from 'src/localization/Trns'
import { CreditCardListModel } from 'src/models/creditCard.models'
import {
  OrganizationInviteModel,
  OrganizationsListResponse,
} from 'src/models/organizations.models'
import Lnk from 'src/navigation/Lnk'
import { budgetOverviewUrl, manageOrgInviteUrl } from 'src/navigation/urls'
import { selectOrganizationInvitesModelsFromState } from 'src/redux/selectors/data/organizations.selectors'
import { selectUserListModelsFromState } from 'src/redux/selectors/data/users.selectors'
import { css } from 'styled-components'

interface BudgetsTableSmallProps {
  budgets: CreditCardListModel[]
  isSlim: boolean
}

const BudgetsTableSmall: React.FC<BudgetsTableSmallProps> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()

  const currencyFormatter = useCurrencyFormatter()
  const users = useSelector(selectUserListModelsFromState)
  const currentUser = useCurrentUser()
  const orgInvites = useSelector(selectOrganizationInvitesModelsFromState)

  return (
    <div>
      {props.budgets.map((budget, i) => {
        const canViewBudget =
          budget?.organizationId === currentUser?.organizationId ||
          budget?.ownerId === currentUser?.id

        let budgetAndInviteOrg: OrganizationsListResponse | undefined
        let correspondingInviteId
        Object.values(orgInvites).forEach((invite: OrganizationInviteModel) => {
          if (invite.organization.id === budget.organizationId) {
            budgetAndInviteOrg = invite.organization
            correspondingInviteId = invite.id
          }
        })

        return (
          <Lnk
            to={budgetOverviewUrl(budget.id)}
            key={i}
            css={css`
              padding: 18px 20px;
              display: flex;
              flex-direction: column;
              min-height: 60px;
              border-top: 1px solid transparent;

              &:hover {
                background-color: ${styleConstants.colors.blue_opacity};

                .animated-table-chevron {
                  transform: translateX(4px);
                }
              }

              &:last-child {
                border-bottom: 1px solid ${styleConstants.colors.gray_light};
              }
            `}
            ariaLabelSource={'Account Overview'}
          >
            <div
              css={css`
                display: flex;
                flex-direction: row;
                width: 100%;
                justify-content: space-between;
                align-items: center;
              `}
            >
              <div
                css={css`
                  display: flex;
                  min-width: 0;
                `}
              >
                <img
                  css={css`
                    height: 20px;
                  `}
                  src={budget.cardTypeImageSrc}
                  alt={''}
                />
                <div
                  css={css`
                    margin-left: 10px;
                    display: flex;
                    flex-direction: column;
                    min-width: 0;
                  `}
                >
                  <span
                    css={css`
                      ${getFontCss(
                        14,
                        themeConstants.colors.font_primary,
                        styleConstants.fontFamilies.medium,
                      )};
                      white-space: nowrap;
                      overflow: hidden;
                      text-overflow: ellipsis;
                      margin-bottom: 4px;
                    `}
                  >
                    {budget.truncatedDisplayName}
                  </span>
                  {!props.isSlim && (
                    <span
                      css={css`
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        ${getFontCss(
                          14,
                          styleConstants.colors.gray_darkest,
                          styleConstants.fontFamilies.regular,
                        )};
                      `}
                    >
                      {users[budget.userId].truncatedFullNameOrEmail}
                    </span>
                  )}
                </div>
              </div>
              <div
                css={css`
                  display: flex;
                  align-items: center;
                `}
              >
                {!canViewBudget ? (
                  <span
                    css={css`
                      margin-right: 30px;
                    `}
                  >
                    {currencyFormatter({
                      amount: 0,
                      currency: budget.currency,
                      maximumFractionDigits: 0,
                    }).replace('0', '********')}
                  </span>
                ) : (
                  <span
                    css={css`
                      margin-right: 30px;
                      ${getFontCss(
                        14,
                        themeConstants.colors.highlight,
                        styleConstants.fontFamilies.regular,
                      )};

                      ${(budget.isInactive || budget.isClosed) &&
                      css`
                        ${getFontCss(
                          14,
                          styleConstants.colors.red,
                          styleConstants.fontFamilies.regular,
                        )};
                      `}
                    `}
                  >
                    {budget.unissuedAmountLabel}
                  </span>
                )}
                <AnimatedTableChevron />
              </div>
            </div>
            {!canViewBudget && (
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  border-radius: 10px;
                  border: 1px solid ${styleConstants.colors.red_dark};
                  background-color: ${styleConstants.colors.red_light};
                  padding: 12px 20px;
                  margin-top: 18px;
                  ${getFontCss(
                    14,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.regular,
                  )};
                  cursor: default;
                `}
                onClick={(e): void => {
                  e.preventDefault()
                }}
                onKeyPress={(e): void => {
                  e.preventDefault()
                }}
                role="button"
                tabIndex={0}
              >
                <span
                  css={css`
                    ${getFontCss(
                      14,
                      styleConstants.colors.red_dark,
                      styleConstants.fontFamilies.medium,
                    )};
                    margin-bottom: 12px;
                  `}
                >
                  {translate('Action Required')}
                </span>
                {translate(
                  'To access the budget, you need to be a part of the org. ${cta}',
                  {
                    cta:
                      budgetAndInviteOrg !== undefined
                        ? translate(
                            'Accept your invite to join ${orgName} to access the budget and the permissions of a budget user.',
                            { orgName: budgetAndInviteOrg.name },
                          )
                        : translate(
                            'Please contact the Owner or Admin to get an invitation.',
                          ),
                  },
                )}
                {correspondingInviteId !== undefined && (
                  <HoverableLink
                    to={manageOrgInviteUrl(correspondingInviteId)}
                    ariaLabelSource={'Review the Invite'}
                    size={HoverableLinkSizes.Large}
                    underline
                    css={css`
                      width: fit-content;
                    `}
                  >
                    <span
                      css={css`
                        display: flex;
                        align-items: center;
                        ${getFontCss(
                          14,
                          themeConstants.colors.cta,
                          styleConstants.fontFamilies.regular,
                        )};
                        margin-top: 8px;
                      `}
                    >
                      {translate('Review the invite')}
                      <SvgArrowForward
                        css={css`
                          transform: scale(calc(16 / 24));
                        `}
                      />
                    </span>
                  </HoverableLink>
                )}
              </div>
            )}
          </Lnk>
        )
      })}
    </div>
  )
}

export default BudgetsTableSmall
